import React, { useMemo } from "react";

export default function MyProfile({ user }) {
  const displayName = user?.name || user?.email?.split("@")[0] || "User";
  const roles = useMemo(
    () => (Array.isArray(user?.roles) ? user.roles.filter((role) => role?.name) : []),
    [user]
  );
  const permissions = useMemo(
    () => (Array.isArray(user?.permissions) ? user.permissions.filter((permission) => permission?.effect === "allow") : []),
    [user]
  );
  const permissionModules = useMemo(
    () => Array.from(new Set(permissions.map((permission) => permission.module).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [permissions]
  );
  const policyCount = useMemo(
    () => Object.keys(user?.policy_summary || {}).length,
    [user]
  );

  return (
    <div style={S.page}>
      <style>{`
        @media (max-width: 980px) {
          .jk-my-profile-metrics,
          .jk-my-profile-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        @media (max-width: 680px) {
          .jk-my-profile-hero {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .jk-my-profile-metrics,
          .jk-my-profile-grid {
            grid-template-columns: 1fr !important;
          }
          .jk-my-profile-identity {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .jk-my-profile-info-row {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
        }
      `}</style>

      <div style={S.hero} className="jk-my-profile-hero">
        <div style={S.identityBlock} className="jk-my-profile-identity">
          <div style={S.avatar}>{displayName.slice(0, 1).toUpperCase()}</div>
          <div>
            <p style={S.eyebrow}>Account Workspace</p>
            <h1 style={S.title}>{displayName}</h1>
            <p style={S.subtitle}>
              Review your account, active role, and available role coverage in one place.
            </p>
          </div>
        </div>
        <div style={S.heroBadge}>
          {user?.effective_role_name || user?.role_name || "User"}
        </div>
      </div>

      <div style={S.metrics} className="jk-my-profile-metrics">
        <MetricCard label="Roles" value={roles.length || 1} hint="Available role switches" />
        <MetricCard label="Permissions" value={permissions.length} hint="Allow rules available" />
        <MetricCard label="Modules" value={permissionModules.length} hint="Distinct access areas" />
        <MetricCard label="Policies" value={policyCount} hint="Policy summary entries" />
      </div>

      <div style={S.grid} className="jk-my-profile-grid">
        <section style={S.card}>
          <h2 style={S.cardTitle}>Profile Details</h2>
          <InfoRow label="Display Name" value={displayName} />
          <InfoRow label="Email" value={user?.email || "—"} mono />
          <InfoRow label="User ID" value={user?.id ?? "—"} />
          <InfoRow label="Active Role" value={user?.effective_role_name || user?.role_name || "—"} />
          <InfoRow label="User Type" value={user?.user_type_name || user?.user_type?.name || user?.user_type_code || "—"} />
          <InfoRow label="SSO User" value={user?.is_sso_user ? "Yes" : "No"} />
        </section>

        <section style={S.card}>
          <h2 style={S.cardTitle}>Available Roles</h2>
          {roles.length === 0 ? (
            <p style={S.emptyText}>No roles are attached to this account.</p>
          ) : (
            <div style={S.chipWrap}>
              {roles.map((role) => (
                <span key={role.id || role.name} style={S.chip}>
                  {role.name}
                </span>
              ))}
            </div>
          )}
        </section>

        <section style={S.card}>
          <h2 style={S.cardTitle}>Accessible Modules</h2>
          {permissionModules.length === 0 ? (
            <p style={S.emptyText}>No frontend modules are currently available for this account.</p>
          ) : (
            <div style={S.chipWrap}>
              {permissionModules.map((moduleName) => (
                <span key={moduleName} style={S.softChip}>
                  {formatLabel(moduleName)}
                </span>
              ))}
            </div>
          )}
        </section>

        <section style={S.card}>
          <h2 style={S.cardTitle}>Access Snapshot</h2>
          <p style={S.summaryText}>
            This view is read-only and reflects the permissions and policy summary currently loaded with your session.
          </p>
          <div style={S.noteBox}>
            <strong style={S.noteTitle}>What this means</strong>
            <p style={S.noteText}>
              If something here looks incorrect, update the mapped roles, features, or policies in the admin console and refresh your session.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

function MetricCard({ label, value, hint }) {
  return (
    <div style={S.metricCard}>
      <div style={S.metricValue}>{value}</div>
      <div style={S.metricLabel}>{label}</div>
      <div style={S.metricHint}>{hint}</div>
    </div>
  );
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div style={S.infoRow} className="jk-my-profile-info-row">
      <span style={S.infoLabel}>{label}</span>
      <span style={{ ...S.infoValue, ...(mono ? S.mono : {}) }}>{value}</span>
    </div>
  );
}

function formatLabel(value) {
  return String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const S = {
  page: {
    display: "grid",
    gap: 18,
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
    padding: 28,
    borderRadius: 30,
    background: "#ffffff",
    border: "1px solid rgba(185,52,58,0.14)",
    boxShadow: "0 24px 70px rgba(185,52,58,0.08)",
  },
  identityBlock: {
    display: "flex",
    alignItems: "center",
    gap: 18,
  },
  avatar: {
    width: 68,
    height: 68,
    borderRadius: 24,
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 950,
    fontSize: 28,
    background: "linear-gradient(135deg, #D73C2E 0%, #B9343A 100%)",
    boxShadow: "0 18px 42px rgba(185,52,58,0.22)",
  },
  eyebrow: {
    margin: "0 0 6px",
    fontSize: 11,
    color: "#D73C2E",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 2,
  },
  title: {
    margin: 0,
    fontSize: 34,
    color: "#252935",
    fontWeight: 950,
    letterSpacing: -0.8,
  },
  subtitle: {
    margin: "10px 0 0",
    color: "#6b6f7d",
    fontSize: 15,
    lineHeight: 1.65,
    maxWidth: 720,
  },
  heroBadge: {
    padding: "12px 18px",
    borderRadius: 999,
    background: "#fff2ef",
    color: "#B9343A",
    border: "1px solid rgba(185,52,58,0.18)",
    fontWeight: 950,
    whiteSpace: "nowrap",
  },
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 16,
  },
  metricCard: {
    padding: 22,
    borderRadius: 24,
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(185,52,58,0.14)",
    boxShadow: "0 16px 40px rgba(37,41,53,0.06)",
  },
  metricValue: {
    fontSize: 28,
    fontWeight: 950,
    color: "#252935",
  },
  metricLabel: {
    marginTop: 8,
    color: "#252935",
    fontSize: 13,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  metricHint: {
    marginTop: 6,
    color: "#6b6f7d",
    fontSize: 13,
    lineHeight: 1.5,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 18,
  },
  card: {
    padding: 24,
    borderRadius: 28,
    background: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(185,52,58,0.14)",
    boxShadow: "0 18px 52px rgba(37,41,53,0.06)",
    display: "grid",
    gap: 14,
  },
  cardTitle: {
    margin: 0,
    fontSize: 18,
    color: "#252935",
    fontWeight: 950,
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "center",
    paddingBottom: 12,
    borderBottom: "1px solid rgba(185,52,58,0.10)",
  },
  infoLabel: {
    color: "#6b6f7d",
    fontSize: 13,
    fontWeight: 800,
  },
  infoValue: {
    color: "#252935",
    fontSize: 14,
    fontWeight: 900,
    textAlign: "right",
  },
  mono: {
    fontFamily: "'Consolas', 'SFMono-Regular', monospace",
    fontSize: 13,
  },
  chipWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  chip: {
    padding: "10px 14px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #D73C2E 0%, #B9343A 100%)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 850,
    boxShadow: "0 12px 28px rgba(185,52,58,0.18)",
  },
  softChip: {
    padding: "10px 14px",
    borderRadius: 999,
    background: "#fff2ef",
    color: "#B9343A",
    border: "1px solid rgba(185,52,58,0.16)",
    fontSize: 13,
    fontWeight: 850,
  },
  emptyText: {
    margin: 0,
    color: "#6b6f7d",
    lineHeight: 1.6,
  },
  summaryText: {
    margin: 0,
    color: "#6b6f7d",
    lineHeight: 1.7,
    fontSize: 14,
  },
  noteBox: {
    padding: 16,
    borderRadius: 20,
    background: "#fff8f4",
    border: "1px solid rgba(185,52,58,0.14)",
  },
  noteTitle: {
    display: "block",
    color: "#252935",
    fontSize: 13,
    fontWeight: 950,
    marginBottom: 6,
  },
  noteText: {
    margin: 0,
    color: "#6b6f7d",
    lineHeight: 1.65,
    fontSize: 14,
  },
};
